/**
 * Os documentos da família, em tabela.
 *
 * Papel: mostra uma linha por documento, com a pessoa, o tipo, o número, as
 * duas datas, o status e a observação, mais os botões de editar e remover.
 *
 * Como usar: quem monta a tabela passa `onEditar` e `onRemover` apenas para
 * quem tem nível `editar`; sem eles a coluna de ações some inteira.
 *
 * A pessoa aparece pelo nome completo com o apelido entre parênteses, por
 * `formatarPessoa` — a mesma forma que o aviso de vencimento usa: dois membros
 * da família compartilham o primeiro nome, e é o nome inteiro que distingue de
 * quem é o passaporte. O nome se repete em todas as linhas da mesma pessoa
 * porque a tabela vem ordenada por pessoa, e omiti-lo nas repetições faria a
 * linha perder a pessoa assim que alguém ordenasse por outra coluna.
 *
 * Cada cabeçalho, menos o das ações, ordena a tabela pela regra
 * `'com-retorno'` de `useOrdenacao`: crescente, decrescente e de volta à ordem
 * em que `documentos` chegou, com o vazio sempre no fim. Pessoa, Documento e
 * Status ordenam pelo texto que a célula mostra — Documento pelo `tipo` como
 * foi gravado, e Status pelo rótulo, e não pelo valor do enum; Emissão e
 * Validade, como data.
 *
 * A marca de vencido ao lado da validade vem da data, e não do `status`: os
 * dois podem discordar, e é a data que manda. Um documento cuja validade passou
 * mostra a pílula do status que está gravado e, ao lado, a validade marcada.
 *
 * Quem responde pela comparação é `jaVenceu`, a mesma função que a apuração do
 * aviso usa — escrevê-la aqui faria o documento aparecer vencido num lugar e em
 * dia no outro no dia em que uma das duas mudasse.
 */
import { type Documento } from '@concierge/shared';

import {
  CabecalhoOrdenavel,
  type ColunaOrdenavel,
  SETA_EM_TEXTO,
  useOrdenacao,
} from '@/shared/embutido';
import { formatarData, formatarPessoa } from '@/shared/lib/formato';
import { jaVenceu } from '@/shared/lib/vencimento';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ROTULOS_STATUS, TOM_DO_STATUS } from '../rotulos';

interface TabelaDocumentosProps {
  documentos: Documento[];
  /** A data de hoje em `AAAA-MM-DD`, para decidir o que já venceu. */
  hoje: string;
  /** Chamado com o documento da linha; ausente esconde a coluna de ações. */
  onEditar?: (documento: Documento) => void;
  /** Chamado com o documento a excluir; a confirmação é de quem monta. */
  onRemover?: (documento: Documento) => void;
}

/** As colunas ordenáveis, na ordem e com o rótulo do cabeçalho. */
const COLUNAS: Record<string, ColunaOrdenavel<Documento> & { rotulo: string }> = {
  pessoa: { rotulo: 'Pessoa', tipo: 'texto', valor: (d) => formatarPessoa(d.pessoa) },
  documento: { rotulo: 'Documento', tipo: 'texto', valor: (d) => d.tipo },
  numero: { rotulo: 'Número', tipo: 'texto', valor: (d) => d.numero },
  emissao: { rotulo: 'Emissão', tipo: 'data', valor: (d) => d.emissao },
  validade: { rotulo: 'Validade', tipo: 'data', valor: (d) => d.validade },
  status: { rotulo: 'Status', tipo: 'texto', valor: (d) => ROTULOS_STATUS[d.status] },
  observacao: { rotulo: 'Obs.', tipo: 'texto', valor: (d) => d.observacao },
};

/**
 * A tabela de documentos.
 *
 * @param documentos - Os documentos a mostrar, na ordem que vale antes de
 *   qualquer clique e à qual o terceiro clique devolve.
 * @param hoje - A data de hoje, de onde sai a marca de vencido.
 * @param onEditar - Chamado ao clicar em editar; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em remover; ausente esconde as ações.
 * @returns A tabela com uma linha por documento.
 */
export function TabelaDocumentos({
  documentos,
  hoje,
  onEditar,
  onRemover,
}: TabelaDocumentosProps) {
  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;
  const ordenacao = useOrdenacao(documentos, COLUNAS, 'com-retorno');

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Object.entries(COLUNAS).map(([chave, coluna]) => (
            <CabecalhoOrdenavel
              key={chave}
              como={TableHead}
              title={`Ordenar por ${coluna.rotulo}`}
              seta={ordenacao.seta(chave)}
              classeDaSeta={SETA_EM_TEXTO}
              onAlternar={() => ordenacao.alternar(chave)}
            >
              {coluna.rotulo}
            </CabecalhoOrdenavel>
          ))}
          {mostrarAcoes && (
            <TableHead>
              <span className="sr-only">Ações</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {ordenacao.linhas.map((documento) => {
          const vencido = jaVenceu(documento.validade, hoje);

          return (
            <TableRow key={documento.id}>
              <TableCell className="text-texto-medio">
                {formatarPessoa(documento.pessoa)}
              </TableCell>
              <TableCell>{documento.tipo}</TableCell>
              <TableCell>{documento.numero ?? '—'}</TableCell>
              <TableCell className="tabular">
                {documento.emissao === null ? '—' : formatarData(documento.emissao)}
              </TableCell>
              <TableCell className="tabular">
                {documento.validade === null ? '—' : formatarData(documento.validade)}
                {vencido && (
                  <span className="ml-2 text-xs font-semibold text-destructive">vencido</span>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={TOM_DO_STATUS[documento.status]} className="w-fit">
                  {ROTULOS_STATUS[documento.status]}
                </Badge>
              </TableCell>
              <TableCell className="text-[12.5px] text-texto-fraco">
                {documento.observacao ?? '—'}
              </TableCell>
              {mostrarAcoes && (
                <TableCell>
                  <div className="flex gap-2">
                    {onEditar !== undefined && (
                      <Button variant="outline" size="sm" onClick={() => onEditar(documento)}>
                        editar
                      </Button>
                    )}
                    {onRemover !== undefined && (
                      <Button variant="destructive" onClick={() => onRemover(documento)}>
                        remover
                      </Button>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
